import { z } from 'zod';
import {
  CONTACT_MESSAGE_PAGE_SIZE_DEFAULT,
  CONTACT_MESSAGE_PAGE_SIZE_MAX,
  FIELD_LIMITS,
} from './constants.js';
import { contactMessageStatusSchema, type ContactMessageStatus } from './enums.js';

const optionalText = (max: number) =>
  z
    .string()
    .max(max)
    .nullish()
    .transform((value) => value?.trim() || null);

export const contactMessageInputSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(FIELD_LIMITS.name),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Email is required')
    .max(FIELD_LIMITS.email)
    .pipe(z.email('Enter a valid email address')),
  phone: optionalText(FIELD_LIMITS.phone),
  company: optionalText(FIELD_LIMITS.contactCompany),
  message: z.string().trim().min(1, 'Message is required').max(FIELD_LIMITS.contactMessage),
  privacyAcknowledged: z.boolean().refine(Boolean, 'Please acknowledge the privacy notice'),
  company_website: z.string().max(0).optional().default(''),
  turnstileToken: z.string().optional(),
});
export type ContactMessageInput = z.input<typeof contactMessageInputSchema>;

export interface ContactMessageReceipt {
  reference: string;
  submittedAt: string;
}

export const contactMessageStatusUpdateSchema = z.object({
  status: contactMessageStatusSchema,
  note: z.string().trim().max(FIELD_LIMITS.noteBody).optional(),
});
export type ContactMessageStatusUpdateInput = z.infer<typeof contactMessageStatusUpdateSchema>;

export const contactMessageNoteSchema = z.object({
  body: z.string().trim().min(1, 'Note cannot be empty').max(FIELD_LIMITS.noteBody),
});
export type ContactMessageNoteInput = z.infer<typeof contactMessageNoteSchema>;

export const adminContactMessageListQuerySchema = z.object({
  q: z.string().trim().max(160).optional(),
  status: contactMessageStatusSchema.optional(),
  submittedFrom: z.iso.date().optional(),
  submittedTo: z.iso.date().optional(),
  sort: z.enum(['submittedAt', 'name', 'status']).default('submittedAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(CONTACT_MESSAGE_PAGE_SIZE_MAX)
    .default(CONTACT_MESSAGE_PAGE_SIZE_DEFAULT),
});
export type AdminContactMessageListQuery = z.infer<typeof adminContactMessageListQuerySchema>;

export interface AdminContactMessageSummary {
  id: string;
  reference: string;
  name: string;
  email: string;
  company: string | null;
  status: ContactMessageStatus;
  submittedAt: string;
}

export interface ContactMessageNoteDto {
  id: string;
  body: string;
  createdAt: string;
  author: { id: string; name: string; email: string } | null;
}

export interface ContactMessageStatusEventDto {
  id: string;
  fromStatus: ContactMessageStatus | null;
  toStatus: ContactMessageStatus;
  createdAt: string;
  actor: { id: string; name: string; email: string } | null;
}

export interface AdminContactMessageDetail extends AdminContactMessageSummary {
  phone: string | null;
  message: string;
  notes: ContactMessageNoteDto[];
  statusEvents: ContactMessageStatusEventDto[];
  updatedAt: string;
}
